import * as csc from 'cornerstone-core';
import {
  toolStyle,
  getToolState,
  addToolState,
  toolColors,
  getModule,
  store,
  BaseToolConfiguration,
} from 'cornerstone-tools';
import * as _ from 'lodash';
import { vhsCursor } from './Cursors';
import drawLineMarker from './drawLineMarker';
import forceImageUpdate from './forceImageUpdate';
import {
  getCanvasDistance,
  projectPerpendicularToAxis,
  projectAlongAxis,
  offsetPixelPointInCanvas,
  mapFloatCoordsToPixels,
  mapFloatCanvasCoordsToPixels,
} from './pixelToCanvasUtils';
import computeVHSAxisLength from 'app/utils/predictions/computeVHSAxisLength';
import handleToolDrag from 'app/CornerstoneTools/VHSToolHandlesImplementation/handleToolDrag';
import {
  moveHandleNearImagePoint,
  EVENTS,
  lineSegDistance,
  BaseAnnotationTool,
  throttle,
  getNewContext,
  draw,
  setShadow,
  drawHandles,
  drawLine,
  drawCircle,
  drawTextBox,
  drawLinkedTextBox,
  drawJoinedLines,
  getHandleNearImagePoint,
} from 'app/CornerstoneTools';
import {
  makeDebouncedLastAnnotationSave,
  restoreLastAnnotation,
} from 'app/CornerstoneTools/utils/toolLastAnnotationStorage';
import normalizeHandlesInCanvas from 'app/CornerstoneTools/utils/normalizeHandlesInCanvas';
import {
  VHSAnnotation,
  EventWithCornerstoneDetail,
  Point,
  InteractionType,
} from 'app/CornerstoneTools/types';
import { IntlShape } from 'react-intl';

const AXIS_VALUE_OFFSET = { x: 20, y: 0 };
Object.freeze(AXIS_VALUE_OFFSET);

const getVHSValue = (vhsData: VHSAnnotation, precision: number): number => {
  const vhsValue = vhsData.longAxisVertebralLength + vhsData.shortAxisVertebralLength;
  const vhsValueWithPrecision = vhsValue.toFixed(precision);
  const vhsValueWithPrecisionNoTrailingZeros = +vhsValueWithPrecision;
  return vhsValueWithPrecisionNoTrailingZeros;
};

const DEFAULT_VHS_KEY = 'vhs';

const DEFAULT_VHS_POINTS = {
  heartBottom: { x: 0.4698095506581004, y: 0.577683553219387 },
  heartLeft: { x: 0.43596494884221004, y: 0.5277182425687069 },
  heartRight: { x: 0.4865532869202915, y: 0.5182096626254964 },
  heartTop: { x: 0.4696676892871766, y: 0.49406843157732727 },
  t4: { x: 0.45765821970032616, y: 0.4540715994422182 },
  t8: { x: 0.5034678338984605, y: 0.4644394949231375 },
};

const _moveEvents = {
  mouse: [EVENTS.MOUSE_DRAG],
  touch: [EVENTS.TOUCH_DRAG],
};

const _moveEndEvents = {
  mouse: [EVENTS.MOUSE_UP, EVENTS.MOUSE_CLICK],
  touch: [EVENTS.TOUCH_END, EVENTS.TOUCH_PINCH, EVENTS.TAP],
};

interface VHSToolConfiguration extends BaseToolConfiguration {
  drawHandles: boolean;
  drawHandlesOnHover: boolean;
  hideHandlesIfMoving: boolean;
  renderDashed: boolean;
  getIntl: () => IntlShape;
}

/**
 * @public
 * @class VHS calculation Tool
 * @memberof Tools.Annotation
 * @classdesc Add a VHS calculation tool in the canvas.
 * @extends Tools.Base.BaseAnnotationTool
 * @hideconstructor
 */
export default class VHSTool extends BaseAnnotationTool {
  protected preventNewMeasurement: boolean = false;
  protected throttledUpdateCachedStats: (
    image: csc.Image,
    element: HTMLElement,
    data: VHSAnnotation
  ) => void;
  protected debouncedLastAnnotationSave: (data: VHSAnnotation) => void;

  constructor(props: Partial<{ configuration: Partial<VHSToolConfiguration> }> = {}) {
    const defaultProps = {
      name: 'VHS',
      supportedInteractionTypes: ['Mouse', 'Touch'] as const,
      configuration: {
        drawHandles: true,
        drawHandlesOnHover: false,
        hideHandlesIfMoving: false,
        renderDashed: false,
        getIntl: () => ({} as IntlShape),
      } as VHSToolConfiguration,
      svgCursor: vhsCursor,
    };

    super(props, defaultProps);

    this.preventNewMeasurement = false;

    this.throttledUpdateCachedStats = throttle(this.updateCachedStats, 110);
    this.debouncedLastAnnotationSave = makeDebouncedLastAnnotationSave('vhs', 500);
  }

  get configuration(): VHSToolConfiguration {
    return super.configuration as VHSToolConfiguration;
  }

  activeCallback = (element: HTMLElement): void => {
    const toolData = getToolState(element, this.name);

    if (toolData && toolData.data.length > 0) return;
    const enabledElement = csc.getEnabledElement(element);

    const restoredLastAnnotation = restoreLastAnnotation(DEFAULT_VHS_KEY) as
      | VHSAnnotation
      | undefined;
    let vhsHandle: Partial<VHSAnnotation>;
    if (restoredLastAnnotation) {
      vhsHandle = {
        handles: _.mapValues(restoredLastAnnotation.handles, (handle) =>
          mapFloatCanvasCoordsToPixels(handle, enabledElement)
        ),
      };
    } else {
      vhsHandle = {
        handles: _.mapValues(DEFAULT_VHS_POINTS, (point) =>
          mapFloatCoordsToPixels(point, enabledElement)
        ),
      };
    }

    this.addToElement(element, vhsHandle);
  };

  // Additional method usable to display tool with precomputed data.
  addToElement = (element: HTMLElement, VHSPoints: Partial<VHSAnnotation> = {}): VHSAnnotation => {
    const { image } = csc.getEnabledElement(element);

    const midWidth = image.width / 2;
    const quarterHeight = (3 * image.height) / 4;

    const measurementData: VHSAnnotation = {
      visible: true,
      active: false,
      color: undefined,
      invalidated: true,
      handles: {
        heartBottom: {
          x: midWidth - midWidth / 3,
          y: quarterHeight,
          allowedOutsideImage: true,
          highlight: true,
          active: false,
        },
        heartLeft: {
          x: midWidth - midWidth / 3 - midWidth / 8,
          y: quarterHeight,
          allowedOutsideImage: true,
          highlight: true,
          active: false,
        },
        heartRight: {
          x: midWidth - midWidth / 3 - midWidth / 7,
          y: quarterHeight - image.height / 6,
          allowedOutsideImage: true,
          highlight: true,
          active: false,
        },
        heartTop: {
          x: midWidth + midWidth / 3,
          y: quarterHeight,
          allowedOutsideImage: true,
          highlight: true,
          active: false,
        },
        t4: {
          x: midWidth + midWidth / 3 + midWidth / 8,
          y: quarterHeight,
          allowedOutsideImage: true,
          highlight: true,
          active: false,
        },
        t8: {
          x: midWidth + midWidth / 3 + midWidth / 7,
          y: quarterHeight - image.height / 6,
          allowedOutsideImage: true,
          highlight: true,
          active: false,
        },
      },
      additionalVertebraPoints: [],
    };

    _.merge(measurementData, VHSPoints);
    this.updateCachedStats(image, element, measurementData);
    addToolState(element, this.name, measurementData);
    forceImageUpdate(element);
    return measurementData;
  };

  /**
   * @brief Disable since we do not want click to create a new tool data.
   */
  createNewMeasurement = (): void => {
    console.log(this.name, 'createNewMeasurement doing nothing');
  };

  pointNearTool = (
    element: HTMLElement,
    data: VHSAnnotation,
    coords: Point,
    interactionType: InteractionType
  ): boolean => {
    if (data.visible === false) {
      return false;
    }
    const nearDistance =
      interactionType === 'mouse' ? store.state.clickProximity : store.state.touchProximity;

    const nearHandleInRange = getHandleNearImagePoint(element, data.handles, coords, nearDistance);
    let nearLongAxis = false;
    if (data.handles.heartTop && data.handles.heartBottom) {
      nearLongAxis =
        lineSegDistance(element, data.handles.heartBottom, data.handles.heartTop, coords) <
        nearDistance;
    }
    let nearShortAxis = false;
    if (data.handles.heartLeft && data.handles.heartRight) {
      nearShortAxis =
        lineSegDistance(element, data.handles.heartLeft, data.handles.heartRight, coords) <
        nearDistance;
    }
    return nearHandleInRange !== undefined || nearLongAxis || nearShortAxis;
  };

  updateCachedStats = (_image: csc.Image, element: HTMLElement, data: VHSAnnotation): void => {
    const { image } = csc.getEnabledElement(element);
    const { longAxisVertebralLength, shortAxisVertebralLength } = computeVHSAxisLength(image, data);
    data.longAxisVertebralLength = longAxisVertebralLength;
    data.shortAxisVertebralLength = shortAxisVertebralLength;
    data.invalidated = false;

    this.debouncedLastAnnotationSave(normalizeHandlesInCanvas(element, data));
  };

  handleSelectedCallback(
    evt: EventWithCornerstoneDetail,
    toolData: VHSAnnotation,
    handle: any,
    interactionType: InteractionType = 'mouse'
  ): void {
    const { element } = evt.detail;
    moveHandleNearImagePoint(evt, this, toolData, handle, interactionType);

    toolData.previousHandles = _.cloneDeep(toolData.handles);
    const handleToolDragForHandle = (evt: EventWithCornerstoneDetail) => {
      handleToolDrag(evt, toolData, handle);
    };

    _.forEach(_moveEvents, (eventTypes) => {
      eventTypes.forEach((eventType) => {
        element.addEventListener(eventType, handleToolDragForHandle as EventListener);
      });
    });
    const moveEndHandler = () => {
      _.forEach(_moveEndEvents, (eventTypes) => {
        eventTypes.forEach((eventType) => {
          element.removeEventListener(eventType, moveEndHandler);
        });
      });

      _.forEach(_moveEvents, (eventTypes) => {
        eventTypes.forEach((eventType) => {
          element.removeEventListener(eventType, handleToolDragForHandle as EventListener);
        });
      });
      delete toolData.previousHandles;
    };
    _.forEach(_moveEndEvents, (eventTypes) => {
      eventTypes.forEach((eventType) => {
        element.addEventListener(eventType, moveEndHandler);
      });
    });
  }

  // Draw projected heart lines along t4->t8 axis
  drawHeartAxisProjection = (
    context: CanvasRenderingContext2D,
    elem: HTMLElement,
    color: string,
    vLength: number,
    t4: Point,
    t8: Point,
    heartPoints: Point[],
    canvasOffset: number
  ): void => {
    const axisProjStart = projectPerpendicularToAxis(elem, t4, t4, t8, canvasOffset);
    const axisDistance = getCanvasDistance(elem, heartPoints[0], heartPoints[1]);
    const axisProjEnd = projectAlongAxis(elem, axisProjStart, t4, t8, axisDistance);

    drawLine(context, elem, axisProjStart, axisProjEnd, { color });
    drawLineMarker(context, elem, color, [axisProjStart, axisProjEnd], 10);

    const axisTextBox = csc.pixelToCanvas(elem, axisProjEnd as csc.PixelCoordinate);

    drawTextBox(context, `${vLength} V`, axisTextBox.x + 10, axisTextBox.y - 20, color);
  };

  drawTool = (eventData: any, toolData: VHSAnnotation, context: CanvasRenderingContext2D): void => {
    const intl = this.configuration.getIntl();
    const { element: elem } = eventData;
    const { handles, longAxisVertebralLength, shortAxisVertebralLength } = toolData;
    const { heartBottom, heartLeft, heartRight, heartTop, t4, t8 } = handles;
    const { handleRadius, drawHandlesOnHover, hideHandlesIfMoving } = this.configuration;
    const { lineDash } = getModule('globalConfiguration').configuration;

    if (t4) t4.movesIndependently = true;
    if (t8) t8.movesIndependently = true;

    setShadow(context, this.configuration);

    // Differentiate the color of activation tool
    const color = toolColors.getColorIfActive(toolData);
    const lineWidth = toolStyle.getToolWidth();

    // Draw vertebral lines
    const vertebralLineOptions = { color, lineDash };
    let linePoints = [t8];

    if (!t4.hasMoved && !t8.hasMoved) {
      const [t5, t6, t7, ...rest] = toolData.additionalVertebraPoints;
      linePoints = _.filter([t5, t6, t7, t8, ...rest]);
      toolData.additionalVertebraPoints.forEach((p) => drawCircle(context, elem, p, 4, { color }));
    }
    drawJoinedLines(context, elem, t4, linePoints, vertebralLineOptions);

    // Draw vertebra name near them to help identification.
    const drawVertebraTextBox = (vertebra: Point, vertebraName: string) => {
      const vertebraTextBoxCoords = offsetPixelPointInCanvas(elem, vertebra, { x: 0, y: 20 });
      drawLinkedTextBox(
        context,
        elem,
        vertebraTextBoxCoords,
        vertebraName,
        null,
        () => [vertebra],
        color,
        lineWidth,
        0,
        true
      );
    };
    drawVertebraTextBox(t4, 'T4');
    drawVertebraTextBox(t8, 'T8');

    const heartLineOptions = { color };

    // Draw heart lines
    drawLine(context, elem, heartTop, heartBottom, heartLineOptions);
    drawLine(context, elem, heartLeft, heartRight, heartLineOptions);

    // Draw projected heart lines along t4, t8 axis
    this.drawHeartAxisProjection(
      context,
      elem,
      color,
      longAxisVertebralLength,
      t4,
      t8,
      [heartTop, heartBottom],
      -30
    );

    const longAxisTextBoxCoords = offsetPixelPointInCanvas(elem, heartBottom, { x: 10, y: 20 });
    const longAxisText = intl.formatMessage({ id: 'tools.VHS.longAxis' });
    drawLinkedTextBox(
      context,
      elem,
      longAxisTextBoxCoords,
      longAxisText,
      null,
      () => [heartBottom],
      color,
      lineWidth,
      0,
      true
    );

    this.drawHeartAxisProjection(
      context,
      elem,
      color,
      shortAxisVertebralLength,
      t4,
      t8,
      [heartLeft, heartRight],
      -60
    );

    const shortAxisTextBoxCoords = offsetPixelPointInCanvas(elem, heartRight, { x: 10, y: -20 });
    const shortAxisText = intl.formatMessage({ id: 'tools.VHS.shortAxis' });
    drawLinkedTextBox(
      context,
      elem,
      shortAxisTextBoxCoords,
      shortAxisText,
      null,
      () => [heartRight],
      color,
      lineWidth,
      0,
      true
    );

    // Draw the handles
    const handleOptions = {
      color,
      handleRadius,
      drawHandlesIfActive: drawHandlesOnHover,
      hideHandlesIfMoving,
    };
    if (this.configuration.drawHandles) {
      drawHandles(context, eventData, toolData.handles, handleOptions);
    }
  };

  renderToolData = (evt: EventWithCornerstoneDetail): void => {
    const eventData = evt.detail;
    const { image, element } = eventData;

    // If we have no toolData for this element, return immediately as there is nothing to do
    const toolData = getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    draw(context, (ctx: CanvasRenderingContext2D) => {
      for (let i = 0; i < toolData.data.length; i += 1) {
        const data = toolData.data[i];
        // Stats must be updated before calling drawTool since values will be used
        // for display.
        if (data.invalidated === true) {
          if (data.longAxisVertebralLength && data.shortAxisVertebralLength) {
            this.throttledUpdateCachedStats(image, element, data);
          } else {
            this.updateCachedStats(image, element, data);
          }
        }
        this.drawTool(evt.detail, data, ctx);
      }
    });
  };
}

export { getVHSValue };
